/**
 * @file StageEditDialog.tsx
 * @description Создание / редактирование стадии воронки (phase09; phase19.31 —
 * разделено: модель формы в `pipelines/stage-form.ts`, поля в
 * `pipelines/StageFormFields.tsx`; нативный флажок заменён общим `Checkbox`).
 *
 * Ошибка проверки или сервера — строкой в окне, окно остаётся открытым.
 */

import { useEffect, useState } from "react";

import { checkStageForm, stageFormFrom, type StageFormValue } from "./pipelines/stage-form";
import { StageFormFields } from "./pipelines/StageFormFields";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import { createStage, updateStage, type PipelineStageDto } from "../../lib/api-crm";

interface Props {
  mode: "create" | "edit";
  pipelineId: string;
  stage?: PipelineStageDto;
  onClose: () => void;
  onSaved: () => void;
}

export function StageEditDialog({ mode, pipelineId, stage, onClose, onSaved }: Props) {
  const notify = useNotify();
  const editing = mode === "edit" && !!stage;
  const [form, setForm] = useState<StageFormValue>(() => stageFormFrom(editing ? stage : undefined));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setForm(stageFormFrom(mode === "edit" ? stage : undefined));
  }, [mode, stage]);

  async function onSubmit() {
    setError(null);
    const check = checkStageForm(form);
    if (!check.ok) {
      setError(check.error);
      return;
    }
    setSaving(true);
    try {
      if (editing && stage) {
        await updateStage(pipelineId, stage.id, { ...check.payload, isActive: form.isActive });
      } else {
        await createStage(pipelineId, check.payload);
      }
      notify.success(editing ? "Стадия обновлена" : "Стадия создана");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить стадию. Попробуйте ещё раз");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Редактирование стадии" : "Новая стадия"}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-y-auto">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <StageFormFields value={form} onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))} editing={editing} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} loading={saving}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
